import type { Table, RowData } from "@tanstack/react-table";
import { getMemoOptions, memo } from "@tanstack/react-table";

/*
Based on the upstream getFacetedUniqueValues utility but with support for nested arrays (e.g. tags)
https://github.com/TanStack/table/blob/main/packages/table-core/src/utils/getFacetedUniqueValues.ts
*/
export function getFacetedUniqueValues<TData extends RowData>(): (
  table: Table<TData>,
  columnId: string,
) => () => Map<string, number> {
  return (table, columnId) =>
    memo(
      () => [table.getColumn(columnId)?.getFacetedRowModel()],
      (facetedRowModel) => {
        const facetedUniqueValues = new Map<string, number>();

        if (!facetedRowModel) return facetedUniqueValues;

        for (const row of facetedRowModel.rows) {
          const values = row.getUniqueValues<string>(columnId);

          for (const value of values) {
            const flattenedValues = Array.isArray(value) ? value : [value];

            for (const flattenedValue of flattenedValues) {
              facetedUniqueValues.set(
                flattenedValue,
                (facetedUniqueValues.get(flattenedValue) ?? 0) + 1,
              );
            }
          }
        }

        return facetedUniqueValues;
      },
      getMemoOptions(
        table.options,
        "debugTable",
        `getFacetedUniqueValues_${columnId}`,
      ),
    );
}
